import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Switch, Drawer } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { infraTypesByIdForIcons } from '../../statics/constants/infraTypes';
import { PlayerChartProvider } from '@/components/General/PlayerChart/context';
import { PlayerChart, TimelinePlayer } from '@/components/General/PlayerChart/components';
HighchartsMore(Highcharts);
import HydraulicProfileService from '@/domain/HydraulicProfiler/HydraulicProfilerService';
import DrawerHeaderWithControls from '@/components/DiagramBlock/DrawerChartPlayer/DrawerHeaderWithControls';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardContext from '../../contexts/DashboardContext.js';

function makePolygonData(xz_coords) {
	if (!xz_coords || !xz_coords.length) return [];
	const points = xz_coords.map(([x, z]) => ({ x, y: z }));
	points.push({ x: xz_coords[0][0], y: xz_coords[0][1] });
	return points;
}

function makeLineData(xz_coords) {
	return (xz_coords || []).map(([x, z]) => ({ x, y: z }));
}

function createNodeSeries(nodes = []) {
	return nodes.map((node) => ({
		type: 'polygon',
		name: node.attributes?.id || 'Node sin ID',
		color: node.color || '#000000',
		data: makePolygonData(node.xz_coords),
		lineWidth: 3,
		customType: 'node',
		attributes: node.attributes,
	}));
}

function createLinkSeries(links = []) {
	return links.map((link) => ({
		type: 'polygon',
		name: link.attributes?.id || 'Link sin ID',
		color: link.color || '#000000',
		data: makePolygonData(link.xz_coords),
		customType: 'link',
		attributes: link.attributes,
	}));
}

function createGroundSeries(ground = []) {
	return ground.map((g, index) => ({
		type: 'line',
		name: `Ground ${index}`,
		color: g.color || '#888888',
		data: makeLineData(g.xz_coords),
		customType: 'ground',
		groundLabel: g.attributes?.id || g.id || g.name || g.label || g.title || `Ground ${index}`,
		enableMouseTracking: false,
		marker: {
			enabled: true,
			symbol: 'square',
		},
	}));
}

function createNodeLabelSeries(nodes = []) {
	const dataForLabels = nodes.map((node) => {
		const { id } = node.attributes || {};
		let maxZ = Number.NEGATIVE_INFINITY;
		let maxX = 0;
		node.xz_coords.forEach(([x, z]) => {
			if (z > maxZ) {
				maxZ = z;
				maxX = x;
			}
		});
		return {
			x: maxX,
			y: maxZ,
			name: id,
		};
	});
	return {
		type: 'scatter',
		name: 'Node Labels',
		id: 'node-labels',
		customType: 'nodeLabel',
		data: dataForLabels,
		marker: {
			enabled: true,
			radius: 4,
			fillColor: 'rgba(0, 0, 0, 0)',
			lineColor: 'rgba(0, 0, 0, 0)',
			lineWidth: 0,
			states: {
				hover: {
					enabled: true,
					fillColor: 'rgba(0, 0, 0, 0)',
					lineColor: 'rgba(0, 0, 0, 0)',
					lineWidth: 0,
				},
			},
		},
		animation: false,
		dataLabels: {
			enabled: false,
			rotation: -90,
			align: 'center',
			verticalAlign: 'bottom',
			x: 0,
			y: -5,
			formatter: function () {
				return this.point.name;
			},
			// Reduce flicker by stabilizing label rendering
			allowOverlap: true,
			crop: false,
			overflow: 'none',
			defer: false,
			style: {
				fontSize: '10px',
				fontWeight: 'bold',
				textOutline: 'none',
			},
		},
		tooltip: {
			enabled: true,
		},
		enableMouseTracking: true,
		zIndex: 999, // ensure labels render above polygons
	};
}

function createWaterLevels(nodes = [], overrideMap) {
	const points = (nodes || [])
		.map((node) => {
			const { id, depth, invert_elev } = node.attributes || {};
			let maxZ = Number.NEGATIVE_INFINITY;
			let anchorX = 0;

			(node.xz_coords || []).forEach(([x, z]) => {
				if (z > maxZ) {
					maxZ = z;
					anchorX = x;
				}
			});

			const safeInvert = Number(invert_elev ?? 0);

			let yValue = null;

			// 1) Use override if provided (from Timeline click)
			if (overrideMap && id != null && overrideMap.has(String(id))) {
				yValue = overrideMap.get(String(id));
			} else {
				// 2) Fallback to first waterLevels entry, or node.waterLevel
				const levels = node.waterLevels;
				if (levels && typeof levels === 'object' && Object.keys(levels).length > 0) {
					const entries = Object.entries(levels)
						.filter(([k]) => Number.isFinite(Number(k)))
						.sort((a, b) => Number(a[0]) - Number(b[0]));
					const lastY = entries.length ? entries[entries.length - 1][1] : null;
					yValue = lastY == null ? null : Number(lastY);
				} else if (node.waterLevel != null) {
					yValue = Number(node.waterLevel);
				}
			}

			// Insert a null point to force a visual break when yValue is missing
			if (yValue == null) {
				return { x: anchorX, y: null, name: id };
			}
			const depthForCustom = Number(yValue);
			const headFromDepth = depthForCustom + safeInvert;

			return {
				x: anchorX,
				y: headFromDepth,
				name: id,
				custom: {
					id,
					depth: depthForCustom,
					invert_elev: safeInvert,
					head: headFromDepth,
				},
			};
		})
		.sort((a, b) => a.x - b.x);

	return {
		type: 'line',
		name: 'Water Levels',
		customType: 'waterLevel',
		data: points,
		color: '#1e90ff',
		lineWidth: 2,
		marker: { enabled: true, symbol: 'square', radius: 4 },
		enableMouseTracking: true,
		animation: false,
		connectNulls: false,
		zIndex: 10,
	};
}

function createWaterLevelsActiveJoin(nodes = [], overrideMap) {
	const points = (nodes || [])
		.map((node) => {
			const { id, invert_elev } = node.attributes || {};
			let maxZ = Number.NEGATIVE_INFINITY;
			let anchorX = 0;
			(node.xz_coords || []).forEach(([x, z]) => {
				if (z > maxZ) {
					maxZ = z;
					anchorX = x;
				}
			});
			const safeInvert = Number(invert_elev ?? 0);
			let yValue = null;
			if (overrideMap && id != null && overrideMap.has(String(id))) {
				yValue = overrideMap.get(String(id));
			} else {
				const levels = node.waterLevels;
				if (levels && typeof levels === 'object' && Object.keys(levels).length > 0) {
					const entries = Object.entries(levels)
						.filter(([k]) => Number.isFinite(Number(k)))
						.sort((a, b) => Number(a[0]) - Number(b[0]));
					const lastY = entries.length ? entries[entries.length - 1][1] : null;
					yValue = lastY == null ? null : Number(lastY);
				} else if (node.waterLevel != null) {
					yValue = Number(node.waterLevel);
				}
			}
			if (yValue == null) return null;
			const headFromDepth = Number(yValue) + safeInvert;
			return { x: anchorX, y: headFromDepth };
		})
		.filter(Boolean)
		.sort((a, b) => a.x - b.x);

	return {
		type: 'line',
		name: 'Water Levels (Active Join)',
		customType: 'waterLevel-active-join',
		data: points,
		color: '#1e90ff',
		lineWidth: 3,
		marker: { enabled: false },
		enableMouseTracking: false,
		animation: false,
		connectNulls: true,
		zIndex: 9,
	};
}

function createInfrastructures(nodes = [], overrideMap) {
	const cdnBase = import.meta.env.VITE_AZURE_CDN;
	const normalizedBase =
		cdnBase && cdnBase.trim().length ? (cdnBase.endsWith('/') ? cdnBase : `${cdnBase}/`) : '';

	const points = (nodes || [])
		.map((node) => {
			if (!node) return null;

			if (!node || node.infrastructureTypeId == null || node.infrastructureTypeName == null)
				return null;

			const { attributes = {}, xz_coords = [] } = node;
			// Find minimum Z and its X anchor
			let minZ = Number.POSITIVE_INFINITY;
			let anchorX = 0;
			xz_coords.forEach(([x, z]) => {
				if (z < minZ) {
					minZ = z;
					anchorX = x;
				}
			});

			const infraTypeId = node.infrastructureTypeId ?? attributes.infrastructureTypeId;
			const iconKey =
				typeof infraTypesByIdForIcons !== 'undefined' && infraTypesByIdForIcons[infraTypeId]
					? infraTypesByIdForIcons[infraTypeId]
					: 'CONDUIT';

			// Decide marker based on waterLevels content
			const wl = node?.waterLevels;
			let markerUrl;
			const nid = attributes?.id != null ? String(attributes.id) : null;
			if (overrideMap && nid && overrideMap.has(nid)) {
				const v = overrideMap.get(nid);
				const isNullish = v === null || v === undefined;
				markerUrl = isNullish
					? `/img/InfraIcons/png/${iconKey}_non_operational.png`
					: `/img/InfraIcons/png/${iconKey}2.png`;
			} else if (wl && typeof wl === 'object' && Object.keys(wl).length > 0) {
				const entries = Object.entries(wl).filter(([k]) => Number.isFinite(Number(k)));
				entries.sort((a, b) => Number(a[0]) - Number(b[0]));
				const lastVal = entries.length ? entries[entries.length - 1][1] : undefined;
				const isNullish = lastVal === null || lastVal === undefined;
				markerUrl = isNullish
					? `/img/InfraIcons/png/${iconKey}_non_operational.png`
					: `/img/InfraIcons/png/${iconKey}2.png`;
			} else {
				// Fallback to previous behavior if no timestamps exist
				markerUrl =
					node.waterLevel == null
						? `/img/InfraIcons/png/${iconKey}_non_operational.png`
						: `/img/InfraIcons/png/${iconKey}2.png`;
			}

			// Attach tooltip metadata from options.nodes: infrastructureTypeName
			const infrastructureTypeName = node.infrastructureTypeName ?? '';
			const idAsset = node.attributes.id;
			return {
				x: anchorX,
				y: minZ,
				custom: { infrastructureTypeName, idAsset },
				marker: {
					enabled: true,
					symbol: `url(${markerUrl})`,
					width: 24,
					height: 24,
				},
			};
		})
		.filter(Boolean)
		.sort((a, b) => a.x - b.x);

	return {
		type: 'scatter',
		name: 'Infrastructures',
		customType: 'infrastructure',
		data: points,
		enableMouseTracking: true,
		color: 'red',
		zIndex: 3,
	};
}

const getChartOptions = (options, waterLevelOverrides) => {
	const nodeSeries = createNodeSeries(options.nodes);
	const linkSeries = createLinkSeries(options.links);
	const groundSeries = createGroundSeries(options.ground);
	const nodeLabelSeries = createNodeLabelSeries(options.nodes);

	// Pass overrides to water levels
	const waterLevels = createWaterLevels(options.nodes, waterLevelOverrides);
	const waterLevelsActiveJoin = createWaterLevelsActiveJoin(options.nodes, waterLevelOverrides);
	const infraStructures = createInfrastructures(options.nodes, waterLevelOverrides);

	const { config } = options;
	const { title, x_axis = {}, y_axis = {}, units = {} } = config || {};
	const zUnit = units?.z ? ` ${units.z}` : '';
	const allSeries = [
		...nodeSeries,
		...linkSeries,
		...groundSeries,
		waterLevelsActiveJoin,
		waterLevels,
		infraStructures,
		nodeLabelSeries,
	];

	return {
		chart: { zoomType: 'xy' },
		legend: { enabled: false },
		title: { text: title || 'Default Title' },
		xAxis: { title: { text: x_axis.title || '' }, min: x_axis.min, max: x_axis.max },
		yAxis: { title: { text: y_axis.title || '' }, min: y_axis.min, max: y_axis.max },
		plotOptions: {
			series: {
				// Disable animations globally to prevent reflow-related flicker
				animation: false,
				states: { inactive: { enabled: false }, hover: { enabled: true } },
			},
		},
		tooltip: {
			formatter: function () {
				const { customType, attributes } = this.series.options || {};
				if (customType === 'link' && attributes) {
					const { id, height, shape, length } = attributes;
					return [
						'Link<br/>',
						`<span style="color: black">id: ${id}<br/>height: ${height.toFixed(3)}<br/>shape: ${shape}<br/>length: ${length.toFixed(3)}</span>`,
					];
				} else if (customType === 'node' && attributes) {
					const { id, invert_elev, depth } = attributes;
					return [
						'Node<br/>',
						`<span style="color: black">id: ${id}<br/>max_depth: ${depth.toFixed(3)}<br/>invert_elev: ${invert_elev.toFixed(3)}</span>`,
					];
				} else if (customType === 'waterLevel' && this.point && this.point.custom) {
					const { id, depth, invert_elev, head } = this.point.custom;
					return [
						'<b>Water Level</b><br/>',
						`<span style="color: black">Sensor name: ${id}<br/>Depth: ${depth.toFixed(3)}${zUnit}<br/>Head: ${head.toFixed(3)}${zUnit}</span>`,
					];
				} else if (customType === 'nodeLabel' && this.point) {
					return `<span style="color: black">${this.point.name}</span>`;
				} else if (customType === 'ground') {
					return `<span style="color: black">${this.series.options.groundLabel || this.series.name}</span>`;
				} else if (customType === 'infrastructure' && this.point && this.point.custom) {
					const { infrastructureTypeName, idAsset } = this.point.custom;
					return [
						`<b>${idAsset}</b><br/>`,
						`<span style="color: black">${infrastructureTypeName}</span>`,
					];
				}
				return `
            <b>${this.series.name}</b><br/>
            X: ${this.x.toFixed(3)}<br/>
            Z: ${this.y.toFixed(3)}
          `;
			},
		},
		series: allSeries,
	};
};

const hydraulicProfileService = new HydraulicProfileService();
const ProfileChart = () => {
	const { profileId } = useParams();
	const navigate = useNavigate();
	const [profileDetails, setProfileDetails] = useState(null);
	const [chartOptions, setChartOptions] = useState(null);
	const [chartSource, setChartSource] = useState(null);
	const { value: context } = useContext(DashboardContext);
	const firsTime = useRef(true);

	// Add a ref to the Highcharts instance so we can update series data directly
	const chartRef = useRef(null);

	const [isDrawerVisible, setIsDrawerVisible] = useState(false);
	const [isChartDataLoading, setIsChartDataLoading] = useState(false);
	const [viewportWidth, setViewportWidth] = useState(
		typeof window === 'undefined' ? 1280 : window.innerWidth,
	);

	const [myNewConfig, setMyNewConfig] = useState(null);

	const handleGetProfileDetails = (startDate, endDate) => {
		hydraulicProfileService
			.getProfileDetails(profileId, startDate, endDate)
			.then((response) => {
				setProfileDetails(response);
			})
			.catch((error) => {
				console.error('Error fetching hydraulic profile details:', error);
			});
	};
	useEffect(() => {
		const startDate = dayjs(context.selectedDate[0]).format('YYYY-MM-DD HH:mm:ss');
		const endDate = dayjs(context.selectedDate[1]).format('YYYY-MM-DD HH:mm:ss');
		handleGetProfileDetails(startDate, endDate);
	}, [profileId, context.selectedDate]);

	useEffect(() => {
		if (profileDetails) {
			const parsed = JSON.parse(profileDetails.structure);
			// Ensure chartSource is available for click handlers
			setChartSource(parsed);
			const options = getChartOptions(parsed);
			setChartOptions(options);
			const chartData = getDataNewChart(parsed);
			setMyNewConfig(chartData);
		}
	}, [profileDetails]);

	// Recompute PlayerChart config when the global date range changes
	useEffect(() => {
		if (chartSource) {
			const chartData = getDataNewChart(chartSource);
			setMyNewConfig(chartData);
		}
	}, [context.selectedDate, chartSource]);

	useEffect(() => {
		if (!firsTime.current) {
			navigate('/');
		} else {
			firsTime.current = false;
		}
	}, [context.selectedLocation]);

	useEffect(() => {
		const handleResize = () => setViewportWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const getDataNewChart = (options) => {
		const nodes = options?.nodes || [];

		const chartData = nodes
			.map((node) => {
				const id = node?.attributes?.id;
				const waterLevels = node?.waterLevels;

				const data =
					waterLevels && typeof waterLevels === 'object'
						? Object.entries(waterLevels)
								.map(([ts, val]) => {
									if (val == null) {
										return { x: Number(ts), y: null };
									}
									const num = Number(val);
									const y = Number.isFinite(num) ? Math.round(num * 1000) / 1000 : null;
									return { x: Number(ts), y };
								})
								.sort((a, b) => a.x - b.x)
						: [];

				return {
					info: {
						name: node?.attributes?.name || id,
						valueUnit: '',
					},
					data,
					paramId: id,
				};
			})
			.filter((item) => item.info != null && item.data.length > 0);

		const dateRange = {
			startDate: dayjs(context.selectedDate[0]).toDate(),
			endDate: dayjs(context.selectedDate[1]).toDate(),
		};

		return {
			chartData,
			playerSpeed: 500,
			dateRange,
		};
	};

	const handleSwitchChange = useCallback((checked) => {
		setIsChartDataLoading(true);
		setIsDrawerVisible(checked);
	}, []);

	const handleDrawerClose = useCallback(() => {
		setIsDrawerVisible(false);
	}, []);

	const drawerReservedSpace = isDrawerVisible
		? viewportWidth >= 1600
			? 120
			: viewportWidth >= 1440
				? 150
				: 270
		: 0;

	// Build overrides from Timeline values and update only Water Levels
	const handleTimelineClickValues = useCallback(
		({ timestamp, values }) => {
			const overrideMap = new Map(
				(values || []).map((v) => [String(v.paramId ?? v.name), v.value]),
			);
			if (!chartSource) return;

			// Update only the water-level series data using Highcharts API
			const chart = chartRef.current?.chart;
			if (chart) {
				const wlSeries = chart.series.find((s) => s?.options?.customType === 'waterLevel');
				const wlActiveJoinSeries = chart.series.find(
					(s) => s?.options?.customType === 'waterLevel-active-join',
				);
				const infraSeries = chart.series.find((s) => s?.options?.customType === 'infrastructure');
				if (wlSeries) {
					const nextData = createWaterLevels(chartSource.nodes, overrideMap).data;
					wlSeries.setData(nextData, false, false);
					if (wlActiveJoinSeries) {
						const nextActiveJoin = createWaterLevelsActiveJoin(chartSource.nodes, overrideMap).data;
						wlActiveJoinSeries.setData(nextActiveJoin, false, false);
					}
					if (infraSeries) {
						const nextInfra = createInfrastructures(chartSource.nodes, overrideMap).data;
						infraSeries.setData(nextInfra, false, false);
					}
					chart.redraw(false);
					return;
				}
			}
			// Fallback: preserve all options, update only water-level data in state
			setChartOptions((prev) => {
				if (!prev || !Array.isArray(prev.series)) return prev;
				const nextWaterLevels = createWaterLevels(chartSource.nodes, overrideMap);
				const nextWaterLevelsActiveJoin = createWaterLevelsActiveJoin(
					chartSource.nodes,
					overrideMap,
				);
				const nextInfra = createInfrastructures(chartSource.nodes, overrideMap);
				const nextSeries = prev.series.map((s) => {
					if (s?.customType === 'waterLevel') return { ...s, data: nextWaterLevels.data };
					if (s?.customType === 'waterLevel-active-join')
						return { ...s, data: nextWaterLevelsActiveJoin.data };
					if (s?.customType === 'infrastructure') return { ...s, data: nextInfra.data };
					return s;
				});
				return { ...prev, series: nextSeries };
			});
		},
		[chartSource],
	);

	return (
		<div style={{ width: '100%', height: '600px', overflow: 'hidden' }} className="mt-[80px] mb-10">
			{chartOptions && (
				<div
					style={{
						height: '100%',
						overflowY: 'auto',
						paddingBottom: `${drawerReservedSpace}px`,
						boxSizing: 'border-box',
					}}
				>
					<HighchartsReact
						highcharts={Highcharts}
						options={chartOptions}
						containerProps={{ style: { minHeight: '600px' } }}
						ref={chartRef} // important: attach chart ref
					/>
				</div>
			)}
			{!chartOptions && <p>Loading chart...</p>}

			{myNewConfig && (
				<PlayerChartProvider
					config={myNewConfig}
					isVisible={true}
					instanceId={'drawer-chart-player'}
				>
					<div className="fixed bottom-5 right-5 z-[1000] bg-white rounded-md p-2 shadow-lg flex items-center justify-center">
						<Switch
							checked={isDrawerVisible}
							onChange={handleSwitchChange}
							checkedChildren="Show Real Time"
							unCheckedChildren="Show Real Time"
							size="default"
						/>
					</div>

					<Drawer
						title={
							<DrawerHeaderWithControls
								title={chartOptions?.title?.text || ''}
								onClose={handleDrawerClose}
							/>
						}
						placement="bottom"
						onClose={handleDrawerClose}
						open={isDrawerVisible}
						height="330px"
						styles={{
							body: { padding: 0, overflow: 'visible' },
							wrapper: { overflow: 'visible' },
						}}
						maskClosable={false}
						closable={false}
						mask={false}
						zIndex={1000}
					>
						<div className="w-full pt-5" style={{ overflow: 'visible' }}>
							<TimelinePlayer onTimestampClick={handleTimelineClickValues} />
							<PlayerChart highChartHeight={200} tooltipPositionTop={true} yAxisTitle="Depth" />
						</div>
					</Drawer>
				</PlayerChartProvider>
			)}
		</div>
	);
};

export default ProfileChart;
